import React, { useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { TableHead, TableRow, TableCell, TableSortLabel, Checkbox } from '@material-ui/core';
import classNames from 'classnames';
import _ from 'lodash';

import RacingDetailContext from '../../Context/RacingDetail.context';
import { toRoman } from '../../../../utils/utils';
import { selectionTypes } from '../../../../utils/content';
import { validName, allGrayedOut, checkIfCrossEvent } from 'src/utils/racingCombinations';
import { RootState } from '../../../../configs/store';
import Icon from '../../../Icon/Icon';
import useIsMobile from 'src/hooks/useIsMobile';
import { RacingPayout } from '../../../../interfaces/RacingState';

interface CompetitorsTableRowProps {
  addingAllBets?: any,
  competitorsSize: number,
  markets?: any
  leg?: string
  isPick?: boolean
  official?: boolean,
  orderBy?: string,
  setOrderBy?: Function,
  order?: 'asc' | 'desc',
  setOrder?: Function,
  shouldWeight: boolean,
  shouldJockey: boolean
  racingType?:string,
  payouts?: RacingPayout,
  index?: string,
  quickcharts?:boolean
}

interface HeadCellProps {
  orderBy: string,
  order: 'asc' | 'desc',
  handleSort: (property: string) => (e) => void, // eslint-disable-line no-unused-vars
  shouldWeight?: boolean,
  shouldJockey?: boolean,
  racingType?:string,
  official?: boolean
  payouts?: RacingPayout,
  quickcharts?: boolean
}

interface FixedOddsHeadProps extends HeadCellProps {
  name: string,
  index: string,
  oddsType: number,
  onChange?: (e) => void, // eslint-disable-line no-unused-vars
  isChecked: boolean,
  isSp: boolean,
  official?: boolean,
  payouts?: RacingPayout
}

interface CheckboxHeadProps {
  name: string,
  checked: boolean
  noTextTransform?: boolean,
  onChange?: (e) => void, // eslint-disable-line no-unused-vars
  onClick?: (e) => void, // eslint-disable-line no-unused-vars
  disabled?: boolean,
  index?: string,
  isPick?: boolean
}


function StartNoHead({ orderBy, order, handleSort, official, quickcharts=false }: HeadCellProps) {
  const { t } = useTranslation('common');

  return (
    <TableCell className={
      classNames({
        'competitors-table__cell': true,
        'competitors-table__cell--quickcharts': quickcharts
      })
    }
    >
      <TableSortLabel
        active={ orderBy === 'startNo' }
        direction={ orderBy === 'startNo' ? order : 'asc' }
        onClick={ handleSort('startNo') }
        className={
          classNames({ 'competitors-table__placeholder-checkbox': true,
            'competitors-table__placeholder-checkbox--quickcharts': quickcharts
          })
        }
        IconComponent={
          () => (
            <div
              className={
                classNames('competitors-table__sort-icon', {
                  [`competitors-table__sort-icon--${ orderBy === 'startNo' ? order : 'asc' }`]: true,
                  'competitors-table__sort-icon--active': orderBy === 'startNo'
                })
              }
            />
          )
        }
      >
        { !quickcharts && t('racingDetail.no') }
      </TableSortLabel>
      {
        !official &&
        <div className={
          classNames({
            'competitors-table__checkbox-container--small': true,
            'competitors-table__checkbox-container--quick-charts': true
          })
        }
        />
      }
    </TableCell>
  );
}

function RunnerHead({ orderBy, order, handleSort, official, quickcharts }: HeadCellProps) {
  const { t } = useTranslation('common');

  return (
    <TableCell className={
      classNames({
        'competitors-table__cell': true,
        'competitors-table__cell--big': quickcharts
      })
    }
    >
      <TableSortLabel
        active={ orderBy === 'horse.name' }
        direction={ orderBy === 'horse.name' ? order : 'asc' }
        onClick={ handleSort('horse.name') }
        className="competitors-table__placeholder-checkbox"
        IconComponent={
          () => (
            <div
              className={
                classNames('competitors-table__sort-icon', {
                  [`competitors-table__sort-icon--${ orderBy === 'horse.name' ? order : 'asc' }`]: true,
                  'competitors-table__sort-icon--active': orderBy === 'horse.name'
                })
              }
            />
          )
        }
      >
        { t('racingDetail.runner') }
      </TableSortLabel>
      {
        !official &&
        <div className="competitors-table__checkbox-container--small" />
      }
    </TableCell>
  );
}

function FixedOddsHead({
  orderBy,
  order,
  handleSort,
  name,
  index,
  oddsType,
  isSp = false,
  official = false,
  onChange,
  isChecked,
  payouts,
  quickcharts = false
}: FixedOddsHeadProps) {
  const { t } = useTranslation('common');

  return (
    <TableCell className={
      classNames({
        'competitors-table__fo-head': true,
        'competitors-table__fo-head--quick-charts': quickcharts
      })
    }
    >
      {
        official && isSp && _.filter(payouts, (payout) => { return payout.startingPrice <= 0; }) ?
          <></>
          :
          <>
            <TableSortLabel
              active={ orderBy === `outcomes[${ index }].value` && name !== 'SP' }
              direction={ orderBy === `outcomes[${ index }].value` ? order : 'asc' }
              onClick={ handleSort(`outcomes[${ index }].value`) }
              className="competitors-table__placeholder-checkbox"
              IconComponent={
                () => {
                  if (name !== 'SP') {
                    return (
                      <div
                        className={
                          classNames('competitors-table__sort-icon', {
                            [`competitors-table__sort-icon--${
                              orderBy === `outcomes[${ index }].value` ? order : 'asc'
                            }`]: name !== 'SP' || official,
                            'competitors-table__sort-icon--active': orderBy === `outcomes[${ index }].value`
                          })
                        }
                      />
                    );
                  } else {
                    return <></>;
                  }
                }
              }
            >
              <div>
                { oddsType === 3 || isSp ? name : t('racingDetail.odds') }
              </div>
            </TableSortLabel>
            {
              (oddsType === 3 || isSp) && !official && <Checkbox
                name="checkBoxSelected"
                color="primary"
                checked={ isChecked }
                value={ name }
                disabled={ false }
                icon={ <Icon icon="checkboxSmall" className="competitors-table__checkbox-container--small" /> }
                onChange={ onChange }
                checkedIcon={
                  <Icon
                    icon="checked"
                    className="competitors-table__checkbox-container--small competitors-table__checkbox-container--small-checked"
                  />
                }
              />
            }
            {
              !official && !(oddsType === 3 || isSp) &&
                <div className="competitors-table__checkbox-container--small" />
            }
          </>
      }
    </TableCell>
  );
}

function RankingHead({ orderBy, order, handleSort }: HeadCellProps) {
  const { t } = useTranslation();
  return (
    <TableCell>
      <TableSortLabel
        active={ orderBy === 'position' }
        direction={ orderBy === 'position' ? order : 'asc' }
        onClick={ handleSort('position') }
        IconComponent={
          () => (
            <div
              className={
                classNames('competitors-table__sort-icon', {
                  [`competitors-table__sort-icon--${ orderBy === 'position' ? order : 'asc' }`]: true,
                  'competitors-table__sort-icon--active': orderBy === 'position'
                })
              }
            />
          )
        }
      >
        { t('racingDetail.rank') }
      </TableSortLabel>
    </TableCell>
  );
}

function DesktopHead({ orderBy, order, handleSort, shouldWeight, shouldJockey, racingType, official }: HeadCellProps) {
  const { t } = useTranslation('common');

  let jockeyLabel = '';

  if (shouldJockey) {
    if (racingType === 'carriages') {
      jockeyLabel = t('racingDetail.driver');
    } else if (racingType === 'horses') {
      jockeyLabel = t('racingDetail.jockey');
    }
  } else {
    jockeyLabel = '';
  }

  const cells = [
    {
      top: {
        label: shouldWeight ? t('racingDetail.weightLabel') : '',
        orderBy: 'weight'
      },
      bottom: {
        label: shouldWeight ? t('racingDetail.ageLabel') : '',
        orderBy: 'horse.bornYear'
      }
    },
    {
      top: {
        label: jockeyLabel,
        orderBy: 'jockey.name'
      },
      bottom: {
        label: shouldJockey ? t('racingDetail.trainer') : '',
        orderBy: 'trainer.name'
      }
    }
  ];

  const buildCells = (cell, condition) => {
    return (
      <TableCell key={ cell.top.orderBy }>
        <TableSortLabel
          active={ orderBy === cell.top.orderBy && cell }
          direction={ orderBy === cell.top.orderBy ? order : 'asc' }
          onClick={ handleSort(cell.top.orderBy) }
          className="competitors-table__head competitors-table__head--highlighted"
          IconComponent={
            () => {
              if (condition && jockeyLabel !== '' && !official) {
                return (
                  <div
                    className={
                      classNames('competitors-table__sort-icon', {
                        [`competitors-table__sort-icon--${ orderBy === cell.top.orderBy ? order : 'asc' }`]: true,
                        'competitors-table__sort-icon--active': orderBy === cell.top.orderBy
                      })
                    }
                  />
                );
              } else {
                return <div></div>;
              }
            }
          }
        >
          { cell.top.label }
        </TableSortLabel>
        <br />
        <TableSortLabel
          active={ orderBy === cell.bottom.orderBy }
          direction={ orderBy === cell.bottom.orderBy ? order : 'asc' }
          onClick={ handleSort(cell.bottom.orderBy) }
          className="competitors-table__head"
          IconComponent={
            () => {
              if (condition) {
                return (
                  <div
                    className={
                      classNames('competitors-table__sort-icon', {
                        [`competitors-table__sort-icon--${ orderBy === cell.bottom.orderBy ? order : 'asc' }`]: true,
                        'competitors-table__sort-icon--active': orderBy === cell.bottom.orderBy
                      })
                    }
                  />
                );
              } else {
                return <div></div>;
              }
            }
          }
        >
          { cell.bottom.label }
        </TableSortLabel>
      </TableCell>
    );
  };

  return (
    <>
      {
        !official ?
          _.map(cells, (cell, index) => {
            if (index === 0)
              return buildCells(cell, shouldWeight);
            else
              return buildCells(cell, shouldJockey);
          })
          :
          <>
            <TableCell colSpan={ 2 } />
          </>
      }
    </>
  );
}

function SingleCheckboxHead({ name, checked, onChange, isPick }: CheckboxHeadProps) {
  return (
    <TableCell>
      <div className={
        classNames({
          'competitors-table__checkbox-container': true,
          'competitors-table__checkbox-container--last': isPick
        })
      }
      >
        <div className="competitors-table__checkbox-title">{ name }</div>
        <Checkbox
          name="checkBoxSelected"
          color="primary"
          checked={ checked }
          onChange={ onChange }
          value={ 1 }
          classes={ { root: '' } }
          icon={ <Icon icon="checkboxSmall" className="competitors-table__checkbox-container--small" /> }
          checkedIcon={
            <Icon
              icon="checked"
              className="competitors-table__checkbox-container--small competitors-table__checkbox-container--small-checked"
            />
          }

        />
      </div>
    </TableCell>
  );
}

function MultiCheckboxHead({ name, checked, noTextTransform, onClick, disabled, index, isPick }: CheckboxHeadProps) {
  const nameChecked = isNaN(parseInt(name)) ? name : toRoman(name);

  return (
    <TableCell>
      <div className={
        classNames({
          'competitors-table__checkbox-container':true,
          'competitors-table__checkbox-container--last': name === 'all',
          'competitors-table__checkbox-container--disabled': disabled
        })
      }
      >
        <div className={
          classNames({
            'competitors-table__checkbox-title': true,
            'competitors-table__checkbox-title--no-text-transform': noTextTransform,
            'competitors-table__checkbox-title--disabled': disabled
          })
        }
        >{ nameChecked !== 'bankers' ? nameChecked : 'B' }</div>
        <Checkbox
          name="checkBoxSelected"
          color="primary"
          checked={ checked }
          value={ isPick ? index : name  }
          disabled={ disabled }
          icon={ <Icon icon="checkboxSmall" className="competitors-table__checkbox-container--small" /> }
          onClick={ onClick }
          checkedIcon={
            <Icon
              icon="checked"
              className="competitors-table__checkbox-container--small competitors-table__checkbox-container--small-checked"
            />
          }
        />
      </div>
    </TableCell>
  );
}

export default function CompetitorsTableHead({
  addingAllBets,
  competitorsSize,
  markets,
  orderBy,
  setOrderBy,
  order,
  setOrder,
  shouldWeight,
  shouldJockey,
  racingType,
  payouts,
  index,
  quickcharts
}: CompetitorsTableRowProps) {
  const {
    selectedMarket,
    event,
    selectedSelectionType,
    selectedHorsePosition,
    setSelectedHorsePosition,
    isValidForQuickBet,
    official
  } = useContext(RacingDetailContext);

  const pickIndex = index;
  const dispatch = useDispatch();
  const { bets } = useSelector((state: RootState) => state.betslip);
  const { t } = useTranslation('common');
  const mobile = useIsMobile();

  const handleSort = (property: string) => (e) => {
    e.preventDefault();

    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const foChecked = (marketName: string) => {
    //This Checks if a single col of FO markets is checked
    const filterMarket = _.filter(markets, { name: marketName });
    let market = filterMarket.length > 0 ? filterMarket[0] : markets[0];

    if (!market){
      market = selectedMarket;
      market.marketName = selectedMarket.name;
    }

    if (!market.outcomes){
      market.outcomes = Array.from({ length: competitorsSize }, (_, i) => i + 1);
    }

    return market.oddsType === 3 && !isValidForQuickBet ?
      (_.filter(bets, { marketName: marketName }).length === competitorsSize) :
      !isValidForQuickBet && (_.filter(bets, { marketName: marketName }).length === _.filter(market.outcomes, { statusNumber: 1 }).length);
  };

  const toggleCheckbox = async (market) => {

    if (isValidForQuickBet || !foChecked(market.name)) {
      if (market){
        addingAllBets(market);
      }

    } else {
      dispatch.betslip.clearBets();
    }
  };

  const checkPositions = (pos: string) => {
    let position = isNaN(+pos) ? selectedHorsePosition.length -1 : +pos -1;

    if (selectedSelectionType && selectedSelectionType !== 'singlePerm' || !selectedSelectionType){

      if (pos === 'bankers' || selectedSelectionType === 'boxed' || !selectedSelectionType){
        position = 0;
      } else {
        position = 1;
      }

    } else if (position < 0){
      position = 0;
    } else if (selectedMarket.crossEvents?.length >= 1){
      position = +pos;
    }

    return position;
  };

  const pickOdds = () => {
    return checkIfCrossEvent(selectedMarket);
  };

  const checkIfChecked = (marketName: string) => {
    // This checks if all column of a market is selected
    const position = checkPositions(marketName);
    const numberOfSelected = !pickOdds() ? selectedHorsePosition[position] : selectedHorsePosition[pickIndex];
    return numberOfSelected?.length === competitorsSize;
  };

  const selectAllPositions = (e) => {
    e.preventDefault();
    const { value } = e.target;
    const position = checkPositions(value);

    if (checkIfChecked(value)) {
      const newSelections = JSON.parse(JSON.stringify(selectedHorsePosition));
      newSelections[position] = [];
      setSelectedHorsePosition(newSelections);
    } else {
      let newSelections = JSON.parse(JSON.stringify(selectedHorsePosition));
      const horseNumbers = _.map(_.filter(event.competitors, { statusNumber: 1 }), (competitor) => +competitor.progNo);
      newSelections[position] = horseNumbers;

      if (position === selectedHorsePosition.length -1){
        // If we select all positions we want to remove previous selections
        newSelections = [...Array(position)].map(() => Array(0));
        newSelections.push(horseNumbers);
      } else {
        newSelections[selectedHorsePosition.length-1] = [];
      }

      setSelectedHorsePosition(newSelections);
    }
  };

  const renderValidHead = () => {
    const checkBoxes = [];
    const name = validName(selectedMarket);

    if (isValidForQuickBet){
      let selectionType = selectedSelectionType !== 'singlePerm' ?
        selectionTypes[selectedSelectionType] :
        selectionTypes[selectedSelectionType][name];
      if (!selectionType){
        selectionType = selectionTypes[name];
      }

      for (let i = 0; i <selectionType?.positions; i += 1) {
        checkBoxes.push({ name: (i + 1).toString() });
      }
      if (selectionType?.bankers) {
        checkBoxes.push({ name: 'bankers' });
      }
      checkBoxes.push({ name: 'all' });
    }

    return checkBoxes;
  };

  return (
    <TableHead>
      <TableRow>
        <StartNoHead orderBy={ orderBy } order={ order } handleSort={ handleSort } official={ official } quickcharts={ quickcharts } />
        <RunnerHead orderBy={ orderBy } order={ order } handleSort={ handleSort } official={ official } quickcharts={ quickcharts } />

        {
          !mobile
          && <DesktopHead
            orderBy={ orderBy }
            order={ order }
            handleSort={ handleSort }
            shouldWeight={ shouldWeight }
            shouldJockey={ shouldJockey }
            racingType={ racingType }
            official={ official }
          />
        }

        {
          _.map(markets, (market, index) =>  {

            return (
              <FixedOddsHead
                key={ market?.name || index }
                orderBy={ orderBy }
                order={ order }
                handleSort={ handleSort }
                name={ market?.name || '' }
                index={ index }
                onChange={ () => toggleCheckbox(market) }
                isChecked = { foChecked(market?.name) }
                oddsType={ selectedMarket.oddsType }
                isSp={ selectedMarket.typeCode === 15100 }
                official={ official }
                payouts={ payouts }
                quickcharts= { quickcharts }
              />
            );
          })
        }

        {
          selectedMarket.oddsType === 2 && selectedMarket.predictionCount === 1 && (
            <SingleCheckboxHead
              name={ !pickOdds() ? selectedMarket.name : t('racingDetail.all') }
              checked={ pickOdds() ? foChecked(selectedMarket.name) : checkIfChecked('all') }
              onChange={ pickOdds() ? () => toggleCheckbox(selectedMarket) : selectAllPositions }
              isPick={ pickOdds() }
            />
          )
        }

        {
          selectedMarket.oddsType === 2 && selectedMarket.predictionCount > 1 && selectedMarket.typeCode !== 15100 && isValidForQuickBet &&
            _.map(renderValidHead(), (checkbox, index) => {
              const disabled = allGrayedOut(selectedHorsePosition, selectedSelectionType) && checkbox.name === 'all';

              return (
                <MultiCheckboxHead
                  key={ index }
                  name={ checkbox.name }
                  index= { pickIndex }
                  checked={ pickOdds() ? checkIfChecked(pickIndex) : checkIfChecked(checkbox.name) }
                  noTextTransform={ isValidForQuickBet }
                  onClick={ !pickOdds() ? selectAllPositions : addingAllBets }
                  disabled={ disabled }
                  isPick={ pickOdds() }
                />
              );
            })
        }
        { official && <RankingHead orderBy={ orderBy } order={ order } handleSort={ handleSort } /> }
      </TableRow>
    </TableHead>
  );
}
